// @ts-nocheck
import React, { ReactElement, useEffect, useState } from 'react';
import {
  Row,
  Column,
  Card,
  Ribbon,
  Carousel,
  Text,
  TextLink,
  Button,
  Spacing,
  useTheme,
  useDevice
} from '@dls/web';
import { CardComponent } from '@singtel-web/widgets';
import { parsePlanDescription } from '@common-modules/product-catalogue';
import { BackArrow as BackArrowIcon } from '@dls/assets/dist/icons/web';
import {
  StyledImg,
  StyledPlusIcon,
  StyledImageCaptionContainer,
  StyledImageListContainer,
  StyledBlackArrow,
  StyledHeroImage
} from './styles';
import ImageSimOnly from '../../assets/images/exclusive-validation/simonly.jpg';
import ImageDevice from '../../assets/images/exclusive-validation/device.jpg';
import { trans as t } from '../../helpers/localisation';
import { getRibbon, setPriceText } from '../../helpers/catalogue-helpers';
import htmlStringToReactComponent from '../../helpers/htmlStringToReactComponent';
import { simOnlyTncUrl } from '../../config/links';
import { APP_TYPE_ANY } from '../../types/common.types';
import { getUrlParams } from '@lux/helpers';
import { isValidArrayWithData, toPlainText } from '../../helpers/common';
import formatPrice from '../../helpers/formatPrice';
import { getPlanTagConfig } from '../../helpers/productDetails';
import AppFlexBox from '../../styles/app-flex';

interface SimOnlyPlusUpSellProps {
  phones: APP_TYPE_ANY;
  hideSimOnlyPlusUpSellScreen: () => void;
  showEligibleDevicesForSimOnlyPlus: () => void;
  proceedSimOnlyPlusWithoutDeviceFlow: () => void;
  navigateProductDetailsPage?: (phone: APP_TYPE_ANY) => APP_TYPE_ANY;
  selectedPlanGroup?: APP_TYPE_ANY;
  selectedPlan: APP_TYPE_ANY;
  topChoicesLimit?: number;
  networkSpeedMap?: string;
}

const SimOnlyPlusUpSell = ({
  phones,
  hideSimOnlyPlusUpSellScreen,
  showEligibleDevicesForSimOnlyPlus,
  proceedSimOnlyPlusWithoutDeviceFlow,
  navigateProductDetailsPage,
  selectedPlanGroup,
  selectedPlan,
  topChoicesLimit,
  networkSpeedMap
}: SimOnlyPlusUpSellProps): ReactElement => {
  const { theme } = useTheme();
  const { isMobile } = useDevice();
  const {
    planName,
    monthlyCharges,
    isCisPlanCatalogue,
    $discountedPrice,
    discountedPrice,
    discountedLiner,
    data,
    talktime,
    sms,
    tags,
    planDescSubLiner,
    handlePlanClick,
    plan,
    upsellRibbon,
    planId,
    ribbon
  } = selectedPlan || {};
  const topChoicesPhones =
    phones.length > topChoicesLimit ? phones.slice(0, topChoicesLimit) : phones;

  const handleTermsAndConditionsClick = () => {
    window.open(simOnlyTncUrl, '_blank', 'noopener,noreferrer');
  };

  const [utmSkipUpsellDevice, setUtmSkipUpsellDevice] = useState(false);
  const { content1, content2, content3 } = parsePlanDescription(
    planDescSubLiner
  );
  const parsedActualPrice = parseFloat(monthlyCharges);
  const parsedDiscountedPrice = parseFloat(
    isCisPlanCatalogue ? $discountedPrice : discountedPrice
  );
  const mainPrice =
    parsedDiscountedPrice >= 0
      ? `${formatPrice(parsedDiscountedPrice)}/mth`
      : `${formatPrice(parsedActualPrice)}/mth`;
  const hasTags = isValidArrayWithData(tags);
  const tagConfig = hasTags ? getPlanTagConfig(tags, networkSpeedMap) : null;
  const bookMarkConfig = ribbon
    ? {
        bookmarkTag: {
          text: ribbon,
          color: '#fff',
          backgroundColor: '#0044CC'
        }
      }
    : {};

  const sanitizeTextData = (value: APP_TYPE_ANY): APP_TYPE_ANY => {
    if (typeof value === 'string') {
      return toPlainText(value);
    }

    if (Array.isArray(value)) {
      return value.map(item => sanitizeTextData(item));
    }

    if (value && typeof value === 'object') {
      return Object.keys(value).reduce((acc, key) => {
        acc[key] = sanitizeTextData(value[key]);
        return acc;
      }, {} as APP_TYPE_ANY);
    }

    return value;
  };

  const cardCompProps = {
    ...bookMarkConfig,
    title: planName,
    content1: sanitizeTextData(content1),
    content2: isValidArrayWithData(content2)
      ? {
          title: t('COMES_WITH'),
        bullets: sanitizeTextData(content2)
        }
      : {},
    content3: isValidArrayWithData(content3)
      ? {
          title: t('PROMOTIONS'),
        bullets: sanitizeTextData(content3),
          imageList: []
        }
      : {},
    tag: tagConfig
      ? {
          text: tagConfig.tagText,
          color: '#fff',
          backgroundColor: tagConfig.tagBgColor
        }
      : null,
    leftCtaType: 'none',
    footerActionLoading: 'success',
    leftText: discountedLiner || '',
    leftMainPrice: mainPrice,
    leftSecondaryText:
      parsedDiscountedPrice < parsedActualPrice
        ? formatPrice(parsedActualPrice)
        : ''
  };

  useEffect(() => {
    const { utm_skipdevice } = getUrlParams(location.search);

    if (utm_skipdevice == 'true') {
      setUtmSkipUpsellDevice(true);
      proceedSimOnlyPlusWithoutDeviceFlow();
    } else {
      setUtmSkipUpsellDevice(false);
    }
  }, []);

  return (
    <Spacing top={isMobile ? 0 : 1}>
      <Row>
        <Column xs={12} sm={4} md={4} lg={4} noGutter>
          <Spacing bottom={3}>
            <Row>
              <AppFlexBox alignItems="center">
                <StyledBlackArrow>
                  <BackArrowIcon color={theme.cl_sec_d1} size={24} />
                </StyledBlackArrow>
                <Spacing top={isMobile ? 0.5 : 0}>
                  <Text type="body">
                    <TextLink secondary onClick={hideSimOnlyPlusUpSellScreen}>
                      {t('PLAN_SELECTION')}
                    </TextLink>
                  </Text>
                </Spacing>
              </AppFlexBox>
            </Row>
          </Spacing>
          {selectedPlan && (
            <Spacing bottom={5}>
              <CardComponent
                {...cardCompProps}
                variant="pricePlanCard"
              ></CardComponent>
            </Spacing>
          )}
        </Column>
        <Column xs={12} sm={8} md={8} lg={8} noGutter>
          <Spacing left={isMobile ? 0 : 10}>
            <Spacing bottom={3}>
              <Text type="header">{t('WANT_DEVICE_WITH_PLAN')}</Text>
            </Spacing>

            <StyledImageListContainer>
              <StyledImageCaptionContainer>
                <StyledImg src={ImageSimOnly} alt="Image of Sim Only" />
                <Text>{t('SIM_ONLY_PLUS_PLAN')}</Text>
              </StyledImageCaptionContainer>
              <StyledPlusIcon />
              <StyledImageCaptionContainer>
                <StyledImg src={ImageDevice} alt="Image of Device" />
                <div>
                  {upsellRibbon && (
                    <Ribbon.Variant1>{upsellRibbon}</Ribbon.Variant1>
                  )}
                  <Text>{t('DEVICE_INSTALLMENT_PLAN')}</Text>
                </div>
              </StyledImageCaptionContainer>
            </StyledImageListContainer>

            <Spacing top={3} bottom={5}>
              <Text>
                {t('SIM_ONLY_PLUS_UPSELL_DESCRIPTION', {
                  tnc: (
                    <TextLink
                      type="smallBody"
                      onClick={handleTermsAndConditionsClick}
                    >
                      {t('TERMS_AND_CONDITIONS')}
                    </TextLink>
                  ) as APP_TYPE_ANY
                })}
              </Text>
            </Spacing>

            <Spacing bottom={3}>
              <Text type="header">{t('TOP_CHOICES_FOR_YOU')}</Text>
            </Spacing>

            <Spacing bottom={3}>
              <Carousel
                type="card"
                infiniteLoop={true}
                outsideButtons={true}
                pagination={2}
              >
                {topChoicesPhones.map(phone => {
                  const ribbonForPlan = getRibbon(phone.ribbon, selectedPlan);
                  return (
                    <Card
                      onClick={() => navigateProductDetailsPage(phone)}
                      key={phone.title}
                    >
                      <Card.Content>
                        {phone.heroImage && (
                          <StyledHeroImage
                            src={phone.heroImage?.src}
                            alt={phone.title}
                          />
                        )}
                        <Column sm={12}>
                          {ribbonForPlan && (
                            <Ribbon.Variant1>
                              {ribbonForPlan?.title || ''}
                            </Ribbon.Variant1>
                          )}
                          <Row>
                            <Text type="header">{phone.title}</Text>
                          </Row>
                          <Row>
                            <Text type="body">{setPriceText(phone)}</Text>
                          </Row>
                        </Column>
                      </Card.Content>
                    </Card>
                  );
                })}
              </Carousel>
            </Spacing>

            <Row>
              <Column>
                <Button
                  fullWidth={true}
                  onClick={showEligibleDevicesForSimOnlyPlus}
                >
                  {t('OK_SHOW_ELIGIBLE_DEVICES')}
                </Button>
              </Column>
              <Column>
                <Button
                  fullWidth={true}
                  secondary
                  onClick={proceedSimOnlyPlusWithoutDeviceFlow}
                >
                  {t('NO_THANKS')}
                </Button>
              </Column>
            </Row>

            {selectedPlanGroup?.footerLiner && (
              <Spacing top={4} bottom={4}>
                {htmlStringToReactComponent(selectedPlanGroup.footerLiner)}
              </Spacing>
            )}
          </Spacing>
        </Column>
      </Row>
    </Spacing>
  );
};

export default SimOnlyPlusUpSell;
